import React, { useState, useEffect } from 'react';
import axios from 'axios';

function formatearMoneda(valor) {
    if (typeof valor !== 'number' || isNaN(valor)) return '$ 0';
    return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(valor);
}

function formatearFecha(fechaStr) {
    if (!fechaStr) return '-';
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function GraficoHistorialPrecios({ historico }) {
    const [puntoHover, setPuntoHover] = useState(null);

    const puntos = [...(historico || [])]
        .filter(h => h && h.precio > 0)
        .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

    if (puntos.length < 2) {
        return (
            <div className="grafico-un-punto">
                <p>📊 <strong>1 registro de precio detectado:</strong> El gráfico de tendencia se trazará automáticamente cuando se registren futuras variaciones de precio para este producto.</p>
            </div>
        );
    }

    const width = 640;
    const height = 220;
    const paddingX = 65;
    const paddingY = 40;

    const precios = puntos.map(p => p.precio);
    const minP = Math.min(...precios);
    const maxP = Math.max(...precios);
    const rangeP = maxP === minP ? 1 : maxP - minP;

    const w = width - paddingX * 2;
    const h = height - paddingY * 2;

    const coords = puntos.map((p, idx) => {
        const x = paddingX + (idx / (puntos.length - 1)) * w;
        const y = height - paddingY - ((p.precio - minP) / rangeP) * h;
        return { x, y, precio: p.precio, fecha: p.fecha };
    });

    const pathD = coords.reduce((acc, c, idx) => `${acc} ${idx === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`, '');
    const areaD = `${pathD} L ${coords[coords.length - 1].x.toFixed(1)} ${height - paddingY} L ${coords[0].x.toFixed(1)} ${height - paddingY} Z`;

    return (
        <div className="grafico-wrapper">
            <svg viewBox={`0 0 ${width} ${height}`} className="grafico-svg">
                <defs>
                    <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0969da" stopOpacity="0.30" />
                        <stop offset="100%" stopColor="#0969da" stopOpacity="0.02" />
                    </linearGradient>
                </defs>

                {/* Líneas de cuadrícula horizontal */}
                <line x1={paddingX} y1={paddingY} x2={width - paddingX} y2={paddingY} stroke="#e1e4e8" strokeDasharray="4 4" />
                <text x={paddingX - 8} y={paddingY + 4} textAnchor="end" fontSize="11" fill="#6e7781">{formatearMoneda(maxP)}</text>

                <line x1={paddingX} y1={height - paddingY} x2={width - paddingX} y2={height - paddingY} stroke="#e1e4e8" strokeDasharray="4 4" />
                <text x={paddingX - 8} y={height - paddingY + 4} textAnchor="end" fontSize="11" fill="#6e7781">{formatearMoneda(minP)}</text>

                {/* Área bajo la curva */}
                <path d={areaD} fill="url(#chartGradient)" />

                {/* Línea principal */}
                <path d={pathD} fill="none" stroke="#0969da" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

                {/* Puntos interactivos */}
                {coords.map((c, idx) => (
                    <g key={idx} onMouseEnter={() => setPuntoHover(c)} onMouseLeave={() => setPuntoHover(null)} style={{ cursor: 'pointer' }}>
                        <circle 
                            cx={c.x} 
                            cy={c.y} 
                            r={c.precio === minP ? 7 : 5} 
                            fill={c.precio === minP ? '#28a745' : '#0969da'} 
                            stroke="#fff" 
                            strokeWidth="2" 
                        />
                    </g>
                ))}
            </svg>

            {/* Tooltip flotante */}
            {puntoHover && (
                <div className="grafico-tooltip">
                    <div className="tooltip-precio">{formatearMoneda(puntoHover.precio)}</div>
                    <div className="tooltip-fecha">{formatearFecha(puntoHover.fecha)}</div>
                </div>
            )}
            
            <div className="grafico-leyenda">
                <span className="leyenda-item"><span className="punto-azul"></span> Evolución</span>
                <span className="leyenda-item"><span className="punto-verde"></span> Mínimo Histórico ({formatearMoneda(minP)})</span>
            </div>
        </div>
    );
}

function DetalleProducto({ producto, alVolver }) {
    const [comparaciones, setComparaciones] = useState([]);
    const [cargandoComp, setCargandoComp] = useState(false);
    const [infoStock, setInfoStock] = useState(null);
    const [cargandoStock, setCargandoStock] = useState(false);
    const [codigoPostal, setCodigoPostal] = useState('');
    const [toastCopiado, setToastCopiado] = useState(false);

    const prodId = producto._id || producto.sku;
    const API_BASE = process.env.REACT_APP_API_URL || (
        (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
            ? `${window.location.protocol}//${window.location.hostname}:3001/api`
            : 'https://dribble-childless-explicit.ngrok-free.dev/api'
    );

    const cargarStock = (cp = null) => {
        setCargandoStock(true);
        const url = cp 
            ? `${API_BASE}/producto/${prodId}/stock?cp=${encodeURIComponent(cp)}`
            : `${API_BASE}/producto/${prodId}/stock`;

        axios.get(url)
            .then(res => setInfoStock(res.data))
            .catch(() => setInfoStock(null))
            .finally(() => setCargandoStock(false));
    };

    useEffect(() => {
        if (!prodId) return;

        // Cargar Comparaciones
        setCargandoComp(true);
        axios.get(`${API_BASE}/producto/${prodId}/comparaciones`)
            .then(res => {
                setComparaciones(res.data.comparaciones || []);
            })
            .catch(() => setComparaciones([]))
            .finally(() => setCargandoComp(false));

        // Cargar Stock inicial
        cargarStock();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prodId]);

    const handleBuscarCP = (e) => {
        e.preventDefault();
        cargarStock(codigoPostal);
    };

    const copiarAlPortapapeles = (texto) => {
        if (navigator.clipboard && window.isSecureContext) {
            return navigator.clipboard.writeText(texto);
        }
        return new Promise((resolve, reject) => {
            try {
                const textArea = document.createElement("textarea");
                textArea.value = texto;
                textArea.style.position = "fixed";
                textArea.style.left = "-999999px";
                textArea.style.top = "-999999px";
                textArea.setAttribute("readonly", "");
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                const exito = document.execCommand('copy');
                document.body.removeChild(textArea);
                if (exito) resolve();
                else reject(new Error("Falló execCommand copy"));
            } catch (err) {
                reject(err);
            }
        });
    };

    const copiarOferta = () => {
        const desc = producto.descuento > 0 ? ` (-${producto.descuento}% OFF)` : '';
        const texto = `🔥 ${producto.nombre} a ${formatearMoneda(producto.price)}${desc} en ${producto.tienda}\n👉 ${producto.url}`;
        copiarAlPortapapeles(texto)
            .then(() => {
                setToastCopiado(true);
                setTimeout(() => setToastCopiado(false), 2500);
            })
            .catch(() => {
                window.prompt('Copia el enlace de la oferta:', texto);
            });
    };

    const cambioPrecio = producto.descuento || 0;

    return (
        <div className="detalle-producto-container">
            {toastCopiado && (
                <div className="toast-copiado-flotante">
                    ✔ ¡Enlace y oferta copiados al portapapeles!
                </div>
            )}

            {/* Barra Superior de Navegación */}
            <div className="detalle-nav-bar">
                <button type="button" onClick={alVolver} className="btn-volver">
                    ⬅ Volver al catálogo
                </button>
                <div className="detalle-tienda-tag">
                    🛍️ {producto.tienda}
                </div>
                <div className="detalle-acciones-nav">
                    <button 
                        type="button" 
                        onClick={copiarOferta} 
                        className="btn-copiar-detalle"
                        title="Copiar oferta formateada para compartir en WhatsApp o redes"
                    >
                        📋 Copiar Oferta
                    </button>
                    <a 
                        href={producto.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="btn-ir-directo"
                        title="Abrir la página oficial del vendedor"
                    >
                        IR A LA TIENDA ↗
                    </a>
                </div>
            </div>

            {/* Cabecera Principal del Producto */}
            <div className="detalle-main-card">
                <div className="detalle-imagen-col">
                    <img 
                        src={producto.imagenUrl} 
                        alt={producto.nombre}
                        onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/400x400/e1e1e1/777?text=Sin+Imagen"; }}
                    />
                    {producto.posibleBug && (
                        <div className="badge-posible-bug-detalle">🔥 POSIBLE BUG / GANGA</div>
                    )}
                    {producto.esMinimoHistorico && (
                        <div className="badge-minimo-historico-detalle">🏆 MÍNIMO HISTÓRICO</div>
                    )}
                    {cambioPrecio > 0 && (
                        <div className="detalle-badge-desc">-{cambioPrecio}% OFF</div>
                    )}
                    {cambioPrecio < 0 && (
                        <div className="detalle-badge-aum">+{Math.abs(cambioPrecio)}% Aumento</div>
                    )}
                </div>

                <div className="detalle-info-col">
                    <span className="detalle-tienda-pill">Vendido por <strong>{producto.tienda}</strong></span>
                    <h1 className="detalle-titulo">{producto.nombre}</h1>

                    <div className="detalle-precios-box">
                        <div className="detalle-precio-actual">
                            {formatearMoneda(producto.price)}
                        </div>
                        {producto.UltPrecio > 0 && producto.UltPrecio !== producto.price && (
                            <div className="detalle-precio-anterior">
                                Precio anterior: <span className="tachado">{formatearMoneda(producto.UltPrecio)}</span>
                            </div>
                        )}
                        {producto.priceSinDescuento > producto.price && (
                            <div className="detalle-precio-lista">
                                Precio de lista: {formatearMoneda(producto.priceSinDescuento)}
                            </div>
                        )}
                    </div>

                    {/* Promociones Activas */}
                    <div className="detalle-seccion-meta">
                        <h4>🎁 Promociones Aplicables:</h4>
                        {producto.promotions?.length > 0 ? (
                            <div className="detalle-promos-list">
                                {producto.promotions.map((p, idx) => (
                                    <span key={idx} className="promo-chip">
                                        🏷️ {p.name} {p.discountPercent ? `(${p.discountPercent}% desc.)` : ''}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="texto-secundario">Sin promociones bancarias o especiales detectadas.</p>
                        )}
                    </div>

                    {/* Especificaciones Técnicas */}
                    <div className="detalle-especificaciones">
                        <div className="espec-item">
                            <span className="espec-label">ID Producto: </span>
                            <span className="espec-valor">{producto.productId || '-'}</span>
                        </div>
                        <div className="espec-item">
                            <span className="espec-label">SKU: </span>
                            <span className="espec-valor">{producto.sku || '-'}</span>
                        </div>
                        <div className="espec-item">
                            <span className="espec-label">EAN / Código de Barra: </span>
                            <span className="espec-valor">{producto.ean || 'No reportado'}</span>
                        </div>
                        <div className="espec-item">
                            <span className="espec-label">Última Detección: </span>
                            <span className="espec-valor">{formatearFecha(producto.timestamp)}</span>
                        </div>
                        <div className="espec-item">
                            <span className="espec-label">Estado de Catálogo: </span>
                            <span className={`espec-valor ${producto.activo !== false ? 'ok-text' : 'no-text'}`}>
                                {producto.activo !== false ? 'Activo en web' : 'Pausado / Sin stock'}
                            </span>
                        </div>
                        {(producto.talles?.length > 0 || infoStock?.tallesDisponibles?.length > 0) && (
                            <div className="espec-item" style={{ gridColumn: '1 / -1', marginTop: '6px' }}>
                                <span className="espec-label">
                                    {/remera|buzo|campera|short|pantalon|calza|musculosa|conjunto|vestido/i.test(producto.nombre || '') ? '👕 Talles con stock:' : '👟 Talles disponibles con stock:'}
                                </span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                                    {(infoStock?.tallesDisponibles || producto.talles).map((t, idx) => (
                                        <span key={idx} style={{ 
                                            backgroundColor: '#e3f2fd', 
                                            color: '#0d47a1', 
                                            border: '1px solid #90caf9', 
                                            padding: '4px 10px', 
                                            borderRadius: '6px', 
                                            fontSize: '13px', 
                                            fontWeight: 'bold' 
                                        }}>
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* SECCIÓN 1: COMPARACIÓN CON OTRAS TIENDAS */}
            <div className="detalle-seccion-panel">
                <div className="seccion-header">
                    <h2>🔍 Comparación de Precios en Otras Tiendas</h2>
                    <span className="seccion-sub">
                        Búsqueda cruzada por código EAN y coincidencia de producto en nuestro catálogo multitienda
                    </span>
                </div>

                {cargandoComp ? (
                    <div className="cargando-box">Buscando el mismo producto en otras tiendas...</div>
                ) : comparaciones.length > 0 ? (
                    <div className="comparaciones-tabla-wrapper">
                        <table className="comparaciones-tabla">
                            <thead>
                                <tr>
                                    <th>Tienda</th>
                                    <th>Producto</th>
                                    <th>Precio</th>
                                    <th>Diferencia con esta tienda</th>
                                    <th>Stock</th>
                                    <th>Coincidencia</th>
                                    <th>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {comparaciones.map((comp, idx) => (
                                    <tr key={idx} className={comp.price < producto.price ? 'fila-mas-barata' : ''}>
                                        <td className="tienda-col">
                                            <strong>{comp.tienda}</strong>
                                        </td>
                                        <td className="nombre-col">
                                            {comp.imagenUrl && (
                                                <img src={comp.imagenUrl} alt="" className="mini-thumb" />
                                            )}
                                            <span>{comp.nombre}</span>
                                        </td>
                                        <td className="precio-col">
                                            {formatearMoneda(comp.price)}
                                        </td>
                                        <td className="dif-col">
                                            {comp.diferenciaPrecio < 0 ? (
                                                <span className="dif-barato">
                                                    Más barata ({comp.porcentajeDiferencia}%)
                                                </span>
                                            ) : comp.diferenciaPrecio > 0 ? (
                                                <span className="dif-caro">
                                                    Más cara (+{comp.porcentajeDiferencia}%)
                                                </span>
                                            ) : (
                                                <span className="dif-igual">Mismo precio</span>
                                            )}
                                        </td>
                                        <td>
                                            {comp.disponible !== false ? (
                                                <span className="badge-stock-online ok" style={{ fontSize: '11px', padding: '3px 8px' }}>✔ En stock</span>
                                            ) : (
                                                <span className="badge-stock-online no" style={{ fontSize: '11px', padding: '3px 8px' }}>✖ Sin stock</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="badge-match">{comp.coincidencia}</span>
                                        </td>
                                        <td>
                                            <a 
                                                href={comp.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer" 
                                                className="btn-ver-tienda-link"
                                            >
                                                Ver en {comp.tienda} ↗
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="sin-datos-box">
                        <p>ℹ️ No se encontraron coincidencias exactas o similares en otras tiendas para este producto.</p>
                    </div>
                )}
            </div>

            {/* SECCIÓN 2: DISPONIBILIDAD Y STOCK EN SUCURSALES */}
            <div className="detalle-seccion-panel">
                <div className="seccion-header">
                    <h2>📍 Disponibilidad y Stock {infoStock?.esNacional !== false ? 'en Todas las Sucursales del País' : `(Zona CP ${infoStock?.codigoPostalConsultado})`}</h2>
                    <span className="seccion-sub">
                        {infoStock?.esNacional !== false 
                            ? 'Relevamiento completo de unidades disponibles y puntos de retiro físico en el país'
                            : `Opciones de entrega y retiro filtradas para el Código Postal ${infoStock?.codigoPostalConsultado}`}
                    </span>
                </div>

                {infoStock?.soportaConsultaEnVivo !== false ? (
                    <div>
                        <form onSubmit={handleBuscarCP} className="buscador-cp-form">
                            <label htmlFor="inputCP">
                                🔍 Filtrar por Código Postal (Opcional):
                            </label>
                            <div className="input-cp-group">
                                <input 
                                    id="inputCP"
                                    type="text" 
                                    placeholder="Ej: 1650, 5000, 2000..." 
                                    value={codigoPostal} 
                                    onChange={(e) => setCodigoPostal(e.target.value)} 
                                />
                                <button type="submit" disabled={cargandoStock} className="btn-consultar-cp">
                                    {cargandoStock ? 'Buscando...' : 'Filtrar Zona'}
                                </button>
                                {codigoPostal && (
                                    <button 
                                        type="button" 
                                        className="btn-reset-cp" 
                                        onClick={() => { setCodigoPostal(''); cargarStock(''); }}
                                    >
                                        Ver Todo el País
                                    </button>
                                )}
                            </div>
                        </form>

                        {cargandoStock ? (
                            <div className="cargando-box">Consultando disponibilidad en tiempo real con {producto.tienda || 'la tienda'}...</div>
                        ) : (
                            <div className="stock-info-live">
                                <div className="stock-online-status" style={{ marginBottom: '15px' }}>
                                    {infoStock?.disponible ? (
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                            <span className="badge-stock-online ok">✔ {infoStock?.mensaje || 'Stock disponible'}</span>
                                            {typeof infoStock?.unidadesDisponibles === 'number' && (
                                                <span className="badge-retiro" style={{ fontSize: '13px', padding: '6px 12px', backgroundColor: '#e1f5fe', color: '#0277bd', fontWeight: 'bold' }}>
                                                    📦 {infoStock.unidadesDisponibles >= 90000 ? 'Stock comercial (+99.999 u.)' : `${infoStock.unidadesDisponibles} unidades disponibles`}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="badge-stock-online no">✖ {infoStock?.mensaje || 'Sin stock disponible reportado'}</span>
                                    )}
                                </div>

                                {infoStock?.sucursales?.length > 0 && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <h4 style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#24292f' }}>
                                            🏬 Sucursales físicas con retiro disponible {infoStock?.esNacional !== false ? 'en el país' : ''} ({infoStock.sucursales.length}):
                                        </h4>
                                        <div className="sucursales-grid">
                                            {infoStock.sucursales.map((suc, idx) => (
                                                <div key={idx} className="sucursal-card">
                                                    <div className="sucursal-nombre">
                                                        🏢 {suc.nombre}
                                                    </div>
                                                    <div className="sucursal-direccion">
                                                        📍 {suc.direccion}
                                                    </div>
                                                    <div className="sucursal-ciudad">
                                                        {suc.localidad} {suc.provincia ? `(${suc.provincia})` : ''}
                                                    </div>
                                                    <div className="sucursal-info-extra">
                                                        <span className="badge-retiro">✔ {suc.modalidad}</span>
                                                        <span className="badge-tiempo">⏳ {suc.tiempoEstimado}</span>
                                                        <span className="badge-costo">💰 {suc.costo}</span>
                                                    </div>
                                                    <div className="sucursal-stock-badge">
                                                        📦 Disponibles {suc.unidades !== undefined ? (suc.unidades >= 90000 ? '+99.999' : suc.unidades) : (infoStock.unidadesDisponibles >= 90000 ? '+99.999' : (infoStock.unidadesDisponibles || 'En stock'))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {infoStock?.envios?.length > 0 && (
                                    <div style={{ marginBottom: '15px' }}>
                                        <h4 style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#24292f' }}>
                                            🚚 Modalidad de Envío a Domicilio:
                                        </h4>
                                        <div className="sucursales-grid">
                                            {infoStock.envios.map((env, idx) => (
                                                <div key={idx} className="sucursal-card" style={{ borderLeft: '4px solid #0969da' }}>
                                                    <div className="sucursal-nombre">
                                                        📦 {env.nombre}
                                                    </div>
                                                    <div className="sucursal-direccion">
                                                        Zona de entrega: {env.zona || (infoStock.codigoPostalConsultado ? `CP ${infoStock.codigoPostalConsultado}` : 'Nacional')}
                                                    </div>
                                                    <div className="sucursal-info-extra" style={{ marginTop: '10px' }}>
                                                        <span className="badge-retiro" style={{ backgroundColor: '#ddf4ff', color: '#0969da' }}>✔ {env.modalidad}</span>
                                                        <span className="badge-tiempo">⏳ {env.tiempoEstimado}</span>
                                                        <span className="badge-costo">💰 {env.costo}</span>
                                                    </div>
                                                    <div className="sucursal-stock-badge" style={{ backgroundColor: '#ddf4ff', color: '#0969da', borderColor: '#b6e3ff' }}>
                                                        📦 Disponibles para entrega {infoStock.unidadesDisponibles !== null && infoStock.unidadesDisponibles !== undefined ? (infoStock.unidadesDisponibles >= 90000 ? '+99.999' : infoStock.unidadesDisponibles) : 'En stock'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {!infoStock?.disponible && (
                                    <div className="sin-datos-box">
                                        <p>⚠️ No se encontró stock para retiro ni entrega a domicilio {infoStock?.codigoPostalConsultado ? `en el código postal ${infoStock.codigoPostalConsultado}` : 'en las zonas consultadas'}.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="stock-online-box">
                        <div className="stock-online-status">
                            {infoStock?.disponible ? (
                                <span className="badge-stock-online ok">✔ {infoStock?.mensaje || 'Stock disponible en tienda online'}</span>
                            ) : (
                                <span className="badge-stock-online no">✖ Sin stock disponible reportado</span>
                            )}
                        </div>
                        <p className="texto-secundario">
                            Esta tienda opera con catálogo digital centralizado. El retiro o envío a domicilio se gestiona directamente en el checkout del vendedor.
                        </p>
                    </div>
                )}
            </div>

            {/* SECCIÓN 3: GRÁFICO Y HISTORIAL DE PRECIOS */}
            {producto.precioHistorico && producto.precioHistorico.length > 0 && (
                <div className="detalle-seccion-panel">
                    <div className="seccion-header">
                        <h2>📈 Evolución Histórica de Precios</h2>
                        <span className="seccion-sub">
                            Seguimiento gráfico y cronológico de todas las variaciones detectadas por nuestro monitor
                        </span>
                    </div>

                    {/* Gráfico SVG interactivo */}
                    <GraficoHistorialPrecios 
                        historico={producto.precioHistorico} 
                    />

                    <div className="historial-tabla-wrapper">
                        <table className="historial-tabla">
                            <thead>
                                <tr>
                                    <th>Fecha y Hora</th>
                                    <th>Precio Registrado</th>
                                    <th>Variación</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {producto.precioHistorico.map((h, i) => (
                                    <tr key={i}>
                                        <td>{formatearFecha(h.fecha)}</td>
                                        <td className="precio-bold">{formatearMoneda(h.precio)}</td>
                                        <td>
                                            {h.descuento !== null && h.descuento !== undefined ? (
                                                h.descuento > 0 ? (
                                                    <span className="dif-barato">-{Number(h.descuento).toFixed(2)}% OFF</span>
                                                ) : h.descuento < 0 ? (
                                                    <span className="dif-caro">+{Math.abs(Number(h.descuento)).toFixed(2)}% Aumento</span>
                                                ) : (
                                                    <span>Sin cambio</span>
                                                )
                                            ) : (
                                                <span className="texto-secundario">Primer registro</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="badge-hist-ok">✔ Verificado</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DetalleProducto;
